import { capitalize, get, isEqual, orderBy, uniqWith } from "lodash";
import moment from "moment";

import { EnumHelpers } from "../../utils/enum-helpers";
import { getSalesUrl } from "../../utils/routes";
import { Enums, Events } from "../../thor.enums";
import { DATE_TIME_FORMAT_SLUG } from "../../thor.const";
import { onlineT } from "../../typings/online";
import { paymentT } from "../../typings/payment";
import { getPaymentStatus } from "../../utils/payment";
import { TranslationsStatus } from "../../typings/translations";
import { auth } from "../../typings/auth";

export class OnlineContainerEditController {
    public static $inject: string[] = [
        "$log",
        "$mdDialog",
        "$mdMedia",
        "$rootScope",
        "$scope",
        "$state",
        "$translate",
        "$window",
        "authService",
        "exportService",
        "onlinePriceService",
        "utilsService",
        "multipleSelect",
        "container",
        "sitesService",
        "toaster"
    ];

    public filterParams: { hidden?: boolean; suppressed?: boolean };
    public hasItemsSold: boolean;
    public selectedTabName;
    public itemsCount: number;
    public isOrgaClientInfosRestricted: boolean;
    public isStatsTabDisplayable: boolean;
    public isStatsActivityRestricted: boolean;
    public supportPhone: string;

    private $t;

    constructor(
        private $log: ng.ILogService,
        private $mdDialog: ng.material.IDialogService,
        private $mdMedia: ng.material.IMedia,
        private $rootScope: ng.IRootScopeService,
        $scope: ng.IScope,
        private $state: ng.ui.IStateService,
        $translate: ng.translate.ITranslateService,
        private $window: ng.IWindowService,
        private authService,
        private exportService,
        private onlinePriceService,
        private utilsService,
        public multipleSelect,
        public container,
        private sitesService,
        private toaster
    ) {
        this.$t = $translate.instant;

        const userRights = authService.getUserRights();
        this.isOrgaClientInfosRestricted = !userRights[auth.Scopes.ORGANIZATION_CLIENT_INFORMATIONS];

        $rootScope.$on(Events.OnlineItemsFilter, (_event, res) => (this.filterParams = res));

        $scope.isNaN = (v: any) => isNaN(v);

        $scope.$watch(
            () => this.container.itemsLoading,
            () => (this.itemsCount = this.container.items.filter(i => !i.states.suppressed).length)
        );
    }

    public $onInit() {
        this.container
            .fetchItems()
            .then(() => {
                this.container.checkIfEveryMandatoryFieldsAreFilled();
                this.container.checkIsPublishable();
                this.container.checkIsReadOnly();
                this.container.startCheckUpdate();
                this.container.fetchOnlineCaution(this.container.id);
            })
            .catch(error => {
                this.$log.error(`Error while fetching items on sale ${this.container.id}`, error);
            });

        this.selectedTabName = this.$state.current.name.split(".").splice(-1).pop();

        // for dev
        this.$log.info(`Online container ${this.container.id} resource : `, this.container.resource);

        this.$rootScope.$on(Events.OnlineItemsSetted, () => (this.hasItemsSold = !!this._getItemsSold().length));

        const userRights = this.authService.getUserRights();
        this.isStatsActivityRestricted = !userRights[auth.Scopes.STATS_ACTIVITY];
        this.isStatsTabDisplayable = this.container.resource?.first_published &&
        (
            this.container.resource.status === "published" ||
            this.container.resource.status === "edited"
        );
        this.supportPhone = this.authService.isInternational()
            ? this.sitesService.contactPhoneInter
            : this.sitesService.contactPhone;

    }

    public handleBackButton() {
        this.container.saleService.cancelItemsQueriesIfRunning();
        this.container.timing.stopInterval();
        this.$window.location.href = getSalesUrl(this.container.resource?.isPast);
    }

    public async initiatePublish(
        { checkTranslationStatus, event }: { checkTranslationStatus?: boolean; event?: PointerEvent } = {
            checkTranslationStatus: true
        }
    ) {
        if (!this.container.isPublishable) {
            this.toaster.show(this.$t("il_n_y_a_rien_a_publier"));
            return;
        }

        if (checkTranslationStatus) {
            await this.container.getSaleTranslationStatus();

            if (this.container.saleTranslationsStatus.status !== TranslationsStatus.Done) {
                this.toaster.show(this.$t("translations.toaster.sale.error"), true);
                return;
            }
        }

        if (this.container.isEveryMandatoryFieldsFilled) {
            this.$mdDialog.show({
                controller: "OnlineSummaryController",
                controllerAs: "onlineSummaryCtrl",
                template: require("./summary.html"),
                locals: {
                    container: this.container
                },
                bindToController: true,
                parent: angular.element(document.body),
                targetEvent: event,
                clickOutsideToClose: false,
                fullscreen: this.$mdMedia("xs")
            });
        } else {
            this.$mdDialog.show({
                controller: "OnlineContainerMissingFieldController",
                controllerAs: "onlineMissingFieldCtrl",
                template: require("./missing-field.html"),
                locals: {
                    container: this.container
                },
                bindToController: true,
                parent: angular.element(document.body),
                targetEvent: event,
                clickOutsideToClose: true,
                fullscreen: this.$mdMedia("xs")
            });
        }
    }

    public getCSV(event, fileType) {
        const itemsSold = this._getItemsSold();
        const maxLength: number = 55;
        const fileName = [
            this.container.resource?.id,
            this.authService.organization?.login,
            this.$t(Enums.exportCsv.fileName[fileType]),
            this.$t("online").toLowerCase(),
            moment(this.container.resource?.datetime).format(DATE_TIME_FORMAT_SLUG)
        ].join("-");
        const columnsHeaders = EnumHelpers.getValues(Enums.exportCsv.columnsHeaders[fileType]);
        const confirm = this.$mdDialog
            .alert()
            .title(this.$t("sale.sale_exports.gdpr_dialog.title"))
            .textContent(this.$t("sale.sale_exports.gdpr_dialog.description"))
            .targetEvent(event)
            .ok(this.$t("j_ai_compris"));
        let dataToExport: any[];

        // 1) Buyers list
        if (fileType === "adj") {
            this.$mdDialog.show(confirm).then(() => {
                dataToExport = itemsSold.map((item: onlineT.Item) => {
                    const lastBidAmount = get(item, "pricing.last_bid.amount");
                    const lastBidCreated = get(item, "pricing.last_bid.created");
                    const subscriptionDate = get(item, "pricing.last_bid.subscription_created");
                    const companyCountry = Enums.UserProCompanyCountry[get(item, "pricing.last_bid.company.country")];
                    const companyVat = get(item, "pricing.last_bid.company.vat");

                    /*
                     * Here we fetch all credit cards infos associated to item payments
                     * and remove duplicates
                     */
                    let paymentCards: paymentT.PaymentCard[] = [];
                    let paymentCard: string;

                    if (item.payment && item.payment.length) {
                        paymentCards = item.payment.map((payment: paymentT.Payment) => payment.card);

                        if (paymentCards.length) {
                            paymentCards = uniqWith(paymentCards, isEqual); // remove duplicates

                            paymentCard = paymentCards
                                .map((card: paymentT.PaymentCard) => {
                                    const { brand, last4, exp_month: expM, exp_year: expY } = card;
                                    const expDate =
                                        expY && expM
                                            ? // credit card expiration date iso format
                                              moment(new Date(expY, expM - 1)).format("MM/YY")
                                            : "";
                                    return `${brand} ${last4 ? `...${last4}` : ""} ${expDate}`;
                                })
                                .join(", ");
                        }
                    }

                    return {
                        type: this.$t(
                            item.pricing?.last_bid?.is_from_order ? "bid_type.automatic_bid" : "bid_type.live_bid"
                        ),
                        order_number: this.utilsService.getOrderNumberNormalized(item),
                        description:
                            item.description.length > maxLength
                                ? `${item.description.substring(0, maxLength)}...`
                                : item.description,
                        last_bid_amount: get(item, "pricing.last_bid.amount"),
                        num_live: get(item, "pricing.last_bid.paddle_number"),
                        first_name: get(item, "pricing.last_bid.first_name"),
                        last_name: get(item, "pricing.last_bid.last_name"),
                        phone_mobile: this.utilsService.formatPhoneNumber(get(item, "pricing.last_bid.phone_mobile")),
                        phone_landline: this.utilsService.formatPhoneNumber(
                            get(item, "pricing.last_bid.phone_landline")
                        ),
                        email: get(item, "pricing.last_bid.email"),
                        address: get(item, "pricing.last_bid.location.address"),
                        postcode: get(item, "pricing.last_bid.location.postcode"),
                        city: get(item, "pricing.last_bid.location.city"),
                        country: this.$t(get(item, "pricing.last_bid.location.country")),
                        payment_card_brand: paymentCard,
                        subscription_date: subscriptionDate ? moment(subscriptionDate).format("L - LT") : undefined,
                        ip: null, // empty
                        last_bid_created: lastBidCreated ? moment(lastBidCreated).format("L - LT") : undefined,
                        id: item.id,
                        final_sold_price: get(item, "finalSoldPrice"),
                        commission_rate_price: this.onlinePriceService.calculateCommissionRatePrice(
                            lastBidAmount,
                            get(this.container, "resource.options.commission_rate.voluntary")
                        ),
                        online_commission_rate_price: this.onlinePriceService.calculateOnlineCommissionRatePrice(
                            lastBidAmount,
                            get(item, "category.field"),
                            { start_at: this.container.resource?.start_at }
                        ),
                        internal_reference: get(item, "internal_reference"),
                        external_reference: get(item, "external_reference"),
                        civility: get(item, "pricing.last_bid.civility"),
                        birthday: moment(get(item, "pricing.last_bid.birthday")).format("L"),
                        company_name: get(item, "pricing.last_bid.company.name"),
                        company_country: companyCountry,
                        vat_status: get(item, "pricing.last_bid.company.status"),
                        vat: companyCountry !== Enums.UserProCompanyCountry.WW ? companyVat : undefined,
                        company_number: get(item, "pricing.last_bid.company.siret"),
                        international_vat: companyCountry === Enums.UserProCompanyCountry.WW ? companyVat : undefined,
                        user_uuid: get(item, "pricing.last_bid.user_id")
                    };
                });

                this.$log.info("Data to export CSV : ", dataToExport);
                this.exportService.exportToCSV(dataToExport, columnsHeaders, fileName);
            });
        }

        // 2) history of 3DS transactions
        if (fileType === "_3ds") {
            const salePaymentDepositAsItem = this.container.salePaymentDeposit.reduce((acc, payment) => {
                const itemPayment = this.container.items.find(i => i.pricing?.last_bid?.user_id === payment.user_id);

                if (itemPayment) {
                    itemPayment.payment = [];
                    itemPayment.payment.push(payment);
                    acc.push(itemPayment);
                }
                return acc;
            }, []);

            this.$mdDialog.show(confirm).then(() => {
                dataToExport = itemsSold.concat(salePaymentDepositAsItem).reduce((acc: any, item: onlineT.Item) => {
                    if (!item.payment) {
                        return acc;
                    }
                    const companyCountry = Enums.UserProCompanyCountry[get(item, "pricing.last_bid.company.country")];
                    const companyVat = get(item, "pricing.last_bid.company.vat");
                    const itemPayments: paymentT.Payment[] = item.payment.filter(
                        p => getPaymentStatus(p) === paymentT.LinkPaymentStatus.SUCCESS
                    );
                    itemPayments.forEach((payment: paymentT.Payment) => {
                        const { method, nature, type } = payment;
                        const paymentType = [
                            this.$t(`payment_method.${method}`),
                            this.$t(`payment_nature.${nature}`),
                            this.$t(`payment_type.${type}`)
                        ].join(" / ");

                        if (acc.find(a => a.tmp_id === payment.id)) {
                            return acc;
                        }

                        return acc.push({
                            date: this._getPaymentDate(payment),
                            civility: get(item, "pricing.last_bid.civility"),
                            first_name: get(item, "pricing.last_bid.first_name"),
                            last_name: get(item, "pricing.last_bid.last_name"),
                            phone_mobile: this.utilsService.formatPhoneNumber(
                                get(item, "pricing.last_bid.phone_mobile")
                            ),
                            phone_landline: this.utilsService.formatPhoneNumber(
                                get(item, "pricing.last_bid.phone_landline")
                            ),
                            email: get(item, "pricing.last_bid.email"),
                            address: get(item, "pricing.last_bid.location.address"),
                            postcode: get(item, "pricing.last_bid.location.postcode"),
                            city: get(item, "pricing.last_bid.location.city"),
                            country: this.$t(get(item, "pricing.last_bid.location.country")),
                            payment_status: this.$t(`payment_status.${getPaymentStatus(payment)}`),
                            payment_error_code: null, // empty
                            payment_organization_comment: get(payment, "link_payment.organization_comment"),
                            payment_amount: Number(get(payment, "amount", 0)),
                            payment_type: paymentType,
                            comment: null, // empty
                            company_name: get(item, "pricing.last_bid.company.name"),
                            vat_status: get(item, "pricing.last_bid.company.status"),
                            international_vat:
                                companyCountry === Enums.UserProCompanyCountry.WW ? companyVat : undefined,
                            vat: companyCountry !== Enums.UserProCompanyCountry.WW ? companyVat : undefined,
                            company_number: get(item, "pricing.last_bid.company.siret"),
                            sale_date:
                                this.container.resource?.datetime ??
                                moment(this.container.resource.datetime).format("L - LT"),
                            sale_name: this.container.resource.name,
                            website: `${capitalize(this.container.resource.payment_provider || "")} / ${this.sitesService.siteName}`,
                            tmp_id: payment.id
                        });
                    });
                    return acc;
                }, []);

                dataToExport = orderBy(
                    dataToExport,
                    ["last_name".toLowerCase(), "first_name".toLowerCase(), "email", "payment_type"],
                    ["asc", "asc", "asc", "asc"]
                );
                this.$log.info("Data to export CSV history of 3DS transactions : ", dataToExport);
                dataToExport.forEach(v => delete v.tmp_id);
                this.exportService.exportToCSV(dataToExport, columnsHeaders, fileName);
            });
        }
    }

    public resetPublish(event) {
        const confirm = this.$mdDialog
            .confirm()
            .ariaLabel(this.$t("confirmation_de_l_annulation_des_modifications"))
            .title(this.$t("confirmation_de_l_annulation_des_modifications"))
            .textContent(
                this.$t(
                    this.container.resource.saleStarted
                        ? "online_sale_purged.i_want_to_undo_all_changes_and_revert_to_last_published_version"
                        : "online_sale_purged.i_want_to_undo_all_changes_and_revert_to_last_scheduled_version"
                )
            )
            .targetEvent(event)
            .ok(this.$t("confirmer"))
            .cancel(this.$t("annuler"));
        this.$mdDialog.show(confirm).then(() =>
            this.container
                .reset()
                .then(() => {
                    this.$rootScope.$broadcast(Events.OnlineSalePurged);
                    this.$rootScope.$broadcast(Events.PostMessageToV2);
                })
                .catch(_error => this.toaster.show(this.$t("une_erreur_est_survenue"), true))
        );
    }

    public shouldDisplayPublicationIcon(): boolean {
        return Boolean(
            this.container.resource?.status === Enums.SaleStatus.Edited &&
            !this.container.isEveryMandatoryFieldsFilled &&
            !this.container.resource?.start_at
        );
    }

    private _getItemsSold(): onlineT.Item[] {
        return this.container.items.filter(
            (item: onlineT.Item) =>
                item.paymentStatus === onlineT.past.ItemStatus.PAID ||
                item.paymentStatus === onlineT.past.ItemStatus.UNPAID
        );
    }

    private _getPaymentDate(payment: paymentT.Payment): string | undefined {
        const cautionPaid: boolean = payment.nature === paymentT.PaymentNature.DEPOSIT;
        const linkPaymentDate = get(payment, "link_payment.paid_date");
        const paymentDate = (cautionPaid ? payment.created : false) || linkPaymentDate;

        return paymentDate ? moment(paymentDate).format("L - LT") : undefined;
    }
}
